import { useState, useEffect, useRef } from "react";
import {
  Upload, Trash2, RefreshCw, Image, Camera, ExternalLink,
  Download, Sparkles
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { API, getAuthHeaders, formatDate } from "./helpers";
import { toast } from "sonner";

// BUG-06 refonte 2026-05-03 — 3 categories FIXES (decision JJ).
// Pas de useConfigList ici : valeurs business fixees.
const PHOTO_CATEGORIES = [
  { value: "avant", label: "AVANT (terrain DAL)", description: "Photos terrain uploadées depuis DAL mobile + manuel" },
  { value: "apres_plan", label: "APRÈS PLAN (IA 3D)", description: "Plans 2D/3D générés par l'IA (DALForm)" },
  { value: "apres_reel", label: "APRÈS RÉEL (post-travaux)", description: "Photos post-travaux uploadées manuellement" },
];

export function PhotosForm({ dossierId, tenantId, verrouille, onUpdate, clientNom, dossierRef, dossier }) {
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [dossierFull, setDossierFull] = useState(dossier || null);
  const fileInputRef = useRef(null);

  // BUG-06 — extraire les sources additionnelles depuis le dossier complet
  // (DAL mobile photos + zones_3d_images generes par DALForm)
  const infosDal = dossierFull?.workflow_data?.tef?.infos_dal || {};
  const dalPhotos = Array.isArray(infosDal.photos) ? infosDal.photos : [];
  const zones_3d_images = infosDal.zones_3d_images || {};

  // Generation nom fichier (manuel uniquement)
  const generateFileName = (category, originalName) => {
    const ext = originalName.split('.').pop()?.toLowerCase() || 'jpg';
    const categoryLabel = PHOTO_CATEGORIES.find(c => c.value === category)?.label || category;
    const clientPart = clientNom ? clientNom.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase() : 'CLIENT';
    const refPart = dossierRef || dossierId.slice(-6);
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return `${refPart}_${clientPart}_${categoryLabel.replace(/[^a-zA-Z0-9]/g, '_')}_${date}.${ext}`;
  };

  useEffect(() => {
    loadPhotos();
    if (!dossier) loadDossier();
  }, [dossierId]);

  // Recharger dossier si la prop change (sync avec parent)
  useEffect(() => {
    if (dossier) setDossierFull(dossier);
  }, [dossier]);

  const loadDossier = async () => {
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setDossierFull(data);
      }
    } catch (err) {
      console.error("Erreur chargement dossier:", err);
    }
  };

  const loadPhotoUrl = async (photoId) => {
    if (photoUrls[photoId]) return;
    try {
      const token = localStorage.getItem('session_token');
      if (!token) return;
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/photos/${photoId}/file`, {
        headers: {
          "X-Tenant-ID": tenantId || "climatpac",
          "Authorization": `Bearer ${token}`
        },
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPhotoUrls(prev => ({ ...prev, [photoId]: url }));
      }
    } catch (err) {
      console.error("Erreur chargement photo:", err);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      photos.forEach(photo => {
        if (!photoUrls[photo.id]) loadPhotoUrl(photo.id);
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [photos, tenantId, dossierId]);

  const loadPhotos = async () => {
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/photos`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPhotos(data);
      }
    } catch (err) {
      console.error("Erreur chargement photos:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length || !selectedCategory) return;
    if (selectedCategory === "apres_plan") {
      toast.error("Catégorie APRÈS PLAN : utilisez le bouton 'Générer 3D' dans le DAL desktop, pas l'upload manuel.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('session_token');
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("categorie", selectedCategory);
        formData.append("nom", generateFileName(selectedCategory, file.name));
        await fetch(`${API}/api/metier/dossiers/${dossierId}/photos`, {
          method: "POST",
          headers: {
            "X-Tenant-ID": tenantId || "climatpac",
            ...(token && { "Authorization": `Bearer ${token}` })
          },
          credentials: 'include',
          body: formData
        });
      }
      loadPhotos();
      setSelectedCategory("");
      toast.success(`${files.length} photo(s) uploadée(s) en ${PHOTO_CATEGORIES.find(c => c.value === selectedCategory)?.label}`);
    } catch (err) {
      console.error("Erreur upload:", err);
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!confirm("Supprimer cette photo ?")) return;
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/photos/${photoId}`, {
        method: "DELETE", headers, credentials: 'include'
      });
      if (res.ok) loadPhotos();
    } catch (err) {
      console.error("Erreur suppression photo:", err);
    }
  };

  const handleViewPhoto = (photo) => {
    const url = photoUrls[photo.id] || photo.url;
    if (url) window.open(url, '_blank');
  };

  const handleDownloadPhoto = async (photo) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/photos/${photo.id}/file`, {
        headers: {
          "X-Tenant-ID": tenantId || "climatpac",
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = photo.nom || 'photo.jpg';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error("Erreur téléchargement photo:", err);
    }
  };

  // Agrégation par catégorie : combine les 3 sources de données
  const photosAvant = [
    ...photos.filter(p => p.categorie === "avant"),
    // DAL mobile photos sont taggees par zone, on les harmonise
    ...dalPhotos.map((dp, i) => ({
      id: `dal_${dp.id || i}`,
      nom: `DAL terrain — ${dp.zone || 'photo'}`,
      url: dp.url,
      created_at: dp.uploaded_at,
      source: "dal_mobile",
      _readonly: true,  // pas de delete pour les photos DAL
    })),
  ];
  // BUG-PHOTOS-02 (2026-05-06) — alignement avec la logique PDF
  // (dal_sections.select_projection_zone) : on n'affiche QUE les zones ayant
  // une URL valide, et on les trie par priorité PMR (SDB > sanitaires >
  // chambre > autres). Évite l'affichage d'une carte « Erreur — Plan IA
  // volets » quand la zone n'a en fait pas de rendu généré.
  const ZONE_PRIORITY_PMR = ["salle_de_bain", "sanitaires", "chambre"];
  const ZONE_LABELS = {
    salle_de_bain: "Salle de bain",
    sanitaires: "Sanitaires (WC)",
    chambre: "Chambre",
    entree: "Entrée",
    salon: "Salon",
    cuisine: "Cuisine",
    couloir: "Couloir",
    escalier: "Escalier",
    exterieur: "Extérieur",
    volets: "Volets",
  };
  const photosApresPlan = Object.entries(zones_3d_images)
    .map(([zoneId, zdata]) => {
      const url = zdata?.image_url || zdata?.url || (typeof zdata === "string" ? zdata : null);
      return { zoneId, zdata, url };
    })
    .filter(({ url }) => Boolean(url))  // masque la zone si pas de rendu
    .map(({ zoneId, zdata, url }) => {
      const prioIdx = ZONE_PRIORITY_PMR.indexOf(zoneId);
      return {
        id: `3d_${zoneId}`,
        nom: `Plan IA — ${ZONE_LABELS[zoneId] || zoneId}`,
        url,
        created_at: zdata?.generated_at || zdata?.created_at,
        source: "ia_3d",
        _readonly: true,
        _priority: prioIdx === -1 ? 999 : prioIdx,
      };
    })
    .sort((a, b) => a._priority - b._priority);
  const photosApresReel = photos.filter(p => p.categorie === "apres_reel");

  const renderPhotoCard = (photo) => (
    <div key={photo.id} className="relative group bg-[var(--oh-bg-elevated)] rounded-lg overflow-hidden">
      <div className="aspect-square w-full max-h-32">
        {(photoUrls[photo.id] || photo.url) ? (
          <img
            src={photoUrls[photo.id] || photo.url}
            alt={photo.nom}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23374151' width='100' height='100'/%3E%3Ctext fill='%239ca3af' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EErreur%3C/text%3E%3C/svg%3E";
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-slate-600">
            <RefreshCw className="w-4 h-4 text-[var(--oh-text-muted)] animate-spin" />
          </div>
        )}
      </div>
      <div className="p-2 bg-[var(--oh-bg-elevated)]">
        <p className="text-[var(--oh-text)] text-xs truncate" title={photo.nom}>{photo.nom}</p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[var(--oh-text-muted)] text-xs">
            {photo.created_at && formatDate(photo.created_at)}
          </span>
          {photo.source === "dal_mobile" && <span className="text-[var(--oh-accent-text)] text-[10px]">📱 DAL</span>}
          {photo.source === "ia_3d" && <span className="text-[var(--oh-accent-text)] text-[10px]">🤖 IA</span>}
        </div>
        <div className="flex items-center justify-center gap-1 mt-2 pt-2 border-t border-[var(--oh-border)]">
          <button
            className="text-cyan-400 hover:text-cyan-300 p-1"
            onClick={() => handleViewPhoto(photo)}
            title="Visualiser"
          >
            <ExternalLink className="w-3 h-3" />
          </button>
          {!photo._readonly && (
            <>
              <button
                className="text-[var(--oh-accent-text)] hover:text-[var(--oh-accent-text)] p-1"
                onClick={() => handleDownloadPhoto(photo)}
                title="Télécharger"
              >
                <Download className="w-3 h-3" />
              </button>
              {!verrouille && (
                <button
                  className="text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)] p-1"
                  onClick={() => handleDeletePhoto(photo.id)}
                  title="Supprimer"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderSection = (title, badge, icon, photosList, emptyMsg) => (
    <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3 border border-[var(--oh-border)]">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[var(--oh-border)]">
        {icon}
        <h4 className="text-[var(--oh-text)] font-medium text-sm flex-1">{title}</h4>
        <span className="text-xs text-[var(--oh-text-secondary)]">{photosList.length}</span>
        {badge}
      </div>
      {photosList.length === 0 ? (
        <div className="text-center py-6 text-[var(--oh-text-muted)] text-xs">{emptyMsg}</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {photosList.map(renderPhotoCard)}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[var(--oh-text)] font-medium">Photos du dossier (Avant / Après Plan / Après Réel)</h3>
      </div>

      {/* Formulaire upload — manuel uniquement (avant + apres_reel) */}
      {!verrouille && (
        <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 mb-4">
          <p className="text-[var(--oh-text)] text-sm mb-3">Ajouter des photos (manuel)</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <p className="text-[var(--oh-text-muted)] text-xs mb-1">Catégorie</p>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                  <SelectValue placeholder="Sélectionner la catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {PHOTO_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCategory === "apres_plan" && (
                <p className="text-[var(--oh-info-text)] text-xs mt-1">
                  ⚠️ Cette catégorie est alimentée par l'IA depuis le DAL desktop, pas par upload manuel.
                </p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                accept="image/*"
                multiple
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={!selectedCategory || uploading || selectedCategory === "apres_plan"}
                className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] w-full sm:w-auto"
              >
                {uploading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Choisir des photos
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  if (selectedCategory && selectedCategory !== "apres_plan") {
                    window.open(`/scanner?type=photo&category=${selectedCategory}&dossierId=${dossierId}`, '_blank');
                  }
                }}
                disabled={!selectedCategory || uploading || selectedCategory === "apres_plan"}
                variant="outline"
                className="border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)] w-full sm:w-auto"
              >
                <Camera className="w-4 h-4 mr-2" />
                Scanner
              </Button>
              <span className="text-[var(--oh-text-secondary)] text-xs">(JPG, PNG, HEIC)</span>
            </div>
          </div>
        </div>
      )}

      {/* 3 sections cote-a-cote (responsive : empile sur mobile) */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-6 h-6 text-[var(--oh-text-muted)] animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {renderSection(
            "AVANT (terrain DAL)",
            null,
            <Camera className="w-4 h-4 text-[var(--oh-info-text)]" />,
            photosAvant,
            "Aucune photo terrain. Uploadez depuis le DAL mobile ou manuellement."
          )}
          {renderSection(
            "APRÈS PLAN (IA 3D)",
            null,
            <Sparkles className="w-4 h-4 text-[var(--oh-accent-text)]" />,
            photosApresPlan,
            "Aucun plan 3D généré. Utilisez 'Générer vue 3D' dans le DAL desktop."
          )}
          {renderSection(
            "APRÈS RÉEL (post-travaux)",
            null,
            <Image className="w-4 h-4 text-[var(--oh-success-text)]" />,
            photosApresReel,
            "Aucune photo post-travaux. Uploadez après le chantier."
          )}
        </div>
      )}
    </div>
  );
}
